<template>
  <v-container>
    <v-toolbar elevation="1" color="white" class="px-2">
      <v-toolbar-title class="text-h6 font-weight-bold">
        <v-icon size="small" class="mr-2" color="primary">mdi-calendar-check</v-icon>
        {{ $filters.capitalize($t(`${moduleName}.label`, 1)) }}
      </v-toolbar-title>

      <v-autocomplete
        v-model="producerFilter"
        :items="producerChoices"
        :item-props="producerProps"
        :label="$filters.capitalize($t('producers.label', 1))"
        prepend-inner-icon="mdi-wind-turbine"
        variant="outlined"
        density="compact"
        hide-details
        clearable
        multiple
        return-object
        class="producer-search mr-4"
      >
        <!-- One line whatever the selection: first producer, then "+N" -->
        <template v-slot:selection="{ item, index }">
          <v-chip v-if="index === 0" size="small" label class="mr-1">
            {{ item.title }}
          </v-chip>
          <span v-else-if="index === 1" class="text-caption text-medium-emphasis">
            +{{ producerFilter.length - 1 }}
          </span>
        </template>
        <template v-slot:no-data>
          <v-list-item>
            <v-list-item-title>
              {{ $filters.capitalize($t("search_for", { noun: $t("producers.label", 1) })) }}
            </v-list-item-title>
          </v-list-item>
        </template>
      </v-autocomplete>

      <!-- Period shown: a window of a few months, the arrows load the previous / next one -->
      <div class="period-nav d-flex align-center mr-4">
        <v-btn
          icon="mdi-chevron-double-left"
          size="small"
          variant="text"
          color="primary"
          :title="$filters.capitalize($t(`${moduleName}.previous_year`))"
          :disabled="loading"
          @click="shiftWindow(-1, 12)"
        ></v-btn>
        <v-btn
          icon="mdi-chevron-left"
          size="small"
          variant="text"
          color="primary"
          :disabled="loading"
          @click="shiftWindow(-1)"
        ></v-btn>
        <div class="period-nav__label text-center">
          <v-icon size="small" class="mr-1">mdi-calendar-range</v-icon>
          {{ monthLabel(windowStart) }} &ndash; {{ monthLabel(windowEnd) }}
        </div>
        <v-btn
          icon="mdi-chevron-right"
          size="small"
          variant="text"
          color="primary"
          :disabled="loading || !canGoForward"
          @click="shiftWindow(1)"
        ></v-btn>
        <v-btn
          icon="mdi-chevron-double-right"
          size="small"
          variant="text"
          color="primary"
          :title="$filters.capitalize($t(`${moduleName}.next_year`))"
          :disabled="loading || !canGoForward"
          @click="shiftWindow(1, 12)"
        ></v-btn>
      </div>

      <v-btn
        color="primary"
        variant="tonal"
        prepend-icon="mdi-refresh"
        class="mr-2"
        :loading="loadingRefresh"
        @click="refreshData"
      >
        {{ $filters.capitalize($t(`${moduleName}.refresh`, 1)) }}
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        prepend-icon="mdi-file-excel-outline"
        :loading="loadingDownloads"
        @click="openExport"
      >
        {{ $filters.capitalize($t("export")) }}
      </v-btn>
    </v-toolbar>

    <!-- Export: pick the months to export -->
    <ExportRangeDialog
      v-model="exportDialog"
      unit="month"
      icon="mdi-file-excel-outline"
      :title="$filters.capitalize($t(`${moduleName}.export_title`))"
      :presets="exportPresets"
      :initial-range="[windowStart, windowEnd]"
      :loading="loadingDownloads"
      @export="download"
    ></ExportRangeDialog>

    <v-card flat class="mt-2">
      <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

      <v-table fixed-header height="calc(100vh - 132px)" class="history-table">
        <thead>
          <tr>
            <th
              scope="col"
              rowspan="2"
              class="sticky-column producer-col sortable"
              :class="{ active: sort.month === null }"
              @click="sortBy(null, null)"
            >
              {{ $filters.capitalize($t("producers.display_name", 1)) }}
              <v-icon size="x-small" class="sort-icon">{{ sortIcon(null, null) }}</v-icon>
            </th>
            <th
              v-for="month in months"
              :key="month"
              scope="col"
              :colspan="columns.length"
              class="month-head text-center"
            >
              {{ monthLabel(month) }}
            </th>
          </tr>
          <tr>
            <template v-for="month in months" :key="month">
              <th
                v-for="(column, columnIndex) in columns"
                :key="month + column.key"
                scope="col"
                class="text-right sub-head sortable"
                :class="{ 'month-start': columnIndex === 0, active: sort.month === month && sort.metric === column.key }"
                :title="$t(`${moduleName}.history.${column.key}`)"
                @click="sortBy(month, column.key)"
              >
                {{ $t(`${moduleName}.history_short.${column.key}`) }}
                <v-icon size="x-small" class="sort-icon">{{ sortIcon(month, column.key) }}</v-icon>
              </th>
            </template>
          </tr>
        </thead>

        <tbody>
          <tr v-if="!loading && visibleRows.length === 0">
            <td :colspan="1 + months.length * columns.length" class="text-center text-disabled py-6">
              {{ $filters.capitalize($t("no_data_text")) }}
            </td>
          </tr>
          <tr v-for="row in visibleRows" :key="row.id">
            <td class="sticky-column producer-col font-weight-medium">{{ row.display_name }}</td>
            <template v-for="(month, index) in row.months" :key="index">
              <td
                v-for="(column, columnIndex) in columns"
                :key="index + column.key"
                class="text-right cell"
                :class="[columnIndex === 0 ? 'month-start' : '', column.percent ? levelClass(month[column.key]) : '']"
                :title="cellTitle(month)"
              >
                <template v-if="month[column.key] === null || month[column.key] === undefined">-</template>
                <template v-else-if="column.percent">{{ formatPercent(month[column.key], column.decimals) }}</template>
                <template v-else>{{ formatEnergy(month[column.key]) }}</template>
              </td>
            </template>
          </tr>
        </tbody>
      </v-table>

      <!-- Color legend for the availability percentages -->
      <div class="d-flex align-center flex-wrap px-3 py-2 text-caption text-medium-emphasis">
        <span class="mr-3">{{ $filters.capitalize($t(`${moduleName}.legend`)) }}</span>
        <span class="legend-swatch level-ok mr-1"></span><span class="mr-4">&ge; {{ thresholds.ok }} %</span>
        <span class="legend-swatch level-warn mr-1"></span><span class="mr-4">{{ thresholds.warn }} &ndash; {{ thresholds.ok }} %</span>
        <span class="legend-swatch level-bad mr-1"></span><span>&lt; {{ thresholds.warn }} %</span>
      </div>
    </v-card>
  </v-container>
</template>

<script>
import moment from "moment";
import { isNone } from "@/functions.js";
import i18n from "@/i18n";
import utils from "@/mixins/utils";
import ExportRangeDialog from "@/components/ExportRangeDialog.vue";

export default {
  components: { ExportRangeDialog },
  mixins: [utils],

  created() {
    this.loadHistory();
  },

  computed: {
    windowStart() {
      return moment(this.windowEnd, "YYYY-MM").subtract(this.monthsPerPage - 1, "months").format("YYYY-MM");
    },
    canGoForward() {
      return this.windowEnd < moment().format("YYYY-MM");
    },
    exportPresets() {
      // The months on screen, ahead of the dialog's own shortcuts
      return [
        { key: "shown", icon: "mdi-monitor", label: this.$t(`${this.moduleName}.export_presets.shown`),
          start: this.windowStart, end: this.windowEnd }
      ];
    },
    months() {
      // Months of the table: always the whole window, whatever the data holds
      const months = [];
      const month = moment(this.windowStart, "YYYY-MM");
      for (let i = 0; i < this.monthsPerPage; i++) {
        months.push(month.format("YYYY-MM"));
        month.add(1, "month");
      }
      return months;
    },
    producerChoices() {
      // Only the producers present in the loaded rows, in table order
      return this.rows.map((row) => ({ pk: row.id, display_name: row.display_name }));
    },
    visibleRows() {
      // Producer filter (selected producers), then the chosen sort
      let rows = this.rows;
      if (this.producerFilter.length > 0) {
        const names = new Set(this.producerFilter.map((p) => p.display_name));
        rows = rows.filter((row) => names.has(row.display_name));
      }
      const { month, metric, desc } = this.sort;
      const index = this.months.indexOf(month);
      const value = (row) => (month === null ? row.display_name : row.months[index]?.[metric]);
      return [...rows].sort((a, b) => {
        const va = value(a);
        const vb = value(b);
        // empty values always last
        if (va === null || va === undefined) return vb === null || vb === undefined ? 0 : 1;
        if (vb === null || vb === undefined) return -1;
        const cmp = month === null
          ? String(va).localeCompare(String(vb), this.$i18n.locale)
          : va - vb;
        return desc ? -cmp : cmp;
      });
    },
    rows() {
      // One row per producer, one entry per window month with the five
      // values (empty when the API has no row for that month)
      const empty = { prod: null, data_turnover: null, time: null, data: null, production: null };
      return this.items.map((item) => {
        const byMonth = {};
        for (const v of item.values || []) {
          byMonth[moment(v.prod.month).format("YYYY-MM")] = {
            prod: v.prod.value,
            data_turnover: v.prod.value_turnover,
            time: v.time,
            data: v.data,
            // monthly production (kWh), stored with the availability row
            production: v.prod.production,
          };
        }
        return {
          id: item.id ?? item.pk ?? item.display_name,
          display_name: item.display_name,
          months: this.months.map((month) => ({ month, ...(byMonth[month] || empty) })),
        };
      });
    },
  },

  methods: {
    sortBy(month, metric) {
      // Same header again: flip the direction; a new one: descending first
      // for values (best / worst at the top), ascending for the producer name
      if (this.sort.month === month && this.sort.metric === metric) {
        this.sort.desc = !this.sort.desc;
      } else {
        this.sort = { month, metric, desc: month !== null };
      }
    },
    sortIcon(month, metric) {
      if (this.sort.month !== month || this.sort.metric !== metric) return "mdi-unfold-more-horizontal";
      return this.sort.desc ? "mdi-arrow-down" : "mdi-arrow-up";
    },
    producerProps(item) {
      if (typeof item !== "object" || item === null) return;
      return { ...item, title: item.display_name, value: item.pk };
    },
    cellTitle(month) {
      // Every metric of the month, on hover
      return this.columns
        .map((c) => {
          const value = month[c.key];
          const text = value === null || value === undefined
            ? "-"
            : c.percent ? `${this.formatPercent(value, c.decimals)} %` : `${this.formatEnergy(value)} kWh`;
          return `${this.$t(`${this.moduleName}.history.${c.key}`)}: ${text}`;
        })
        .join("\n");
    },
    shiftWindow(direction, months = this.monthsPerPage) {
      // Previous / next window (by monthsPerPage, or by a year), always
      // monthsPerPage months wide and never past the current month
      let end = moment(this.windowEnd, "YYYY-MM").add(direction * months, "months");
      const now = moment().startOf("month");
      if (end.isAfter(now)) end = now;
      this.windowEnd = end.format("YYYY-MM");
      this.loadHistory();
    },
    openExport() {
      this.exportDialog = true;
    },
    monthLabel(month) {
      const label = moment(month).locale(this.$i18n.locale).format("MMMM YYYY");
      return label.charAt(0).toUpperCase() + label.slice(1);
    },
    formatPercent(value, decimals) {
      return Number(value).toLocaleString(this.$i18n.locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    },
    formatEnergy(value) {
      return Number(value).toLocaleString(this.$i18n.locale, { maximumFractionDigits: 0 });
    },
    levelClass(value) {
      if (isNone(value)) return "";
      if (value >= this.thresholds.ok) return "level-ok";
      if (value >= this.thresholds.warn) return "level-warn";
      return "level-bad";
    },
    async loadHistory() {
      const start = this.windowStart;
      const end = this.windowEnd;
      this.loading = true;
      try {
        // Plain dates (no time zone): a local midnight sent with an offset
        // becomes the previous day in UTC on the API, dropping the last month
        const items = await this.$store.dispatch(`${this.moduleName}/getHistory`, {
          start: `${start}-01`,
          end: `${end}-01`,
        });
        this.items = Array.isArray(items) ? items : [];
        // keep only selected producers still present in the new period
        const names = new Set(this.rows.map((row) => row.display_name));
        this.producerFilter = this.producerFilter.filter((p) => names.has(p.display_name));
      } finally {
        this.loading = false;
      }
    },
    async refreshData() {
      const currentUser = isNone(this.$store.getters.user) ? undefined : this.$store.getters.user.id;
      this.loadingRefresh = true;
      this.$store.dispatch("snackbar/showSuccess", this.$t("snack.refresh"));
      try {
        await this.$store.dispatch(`${this.moduleName}/refresh`, { user: currentUser });
        await this.loadHistory();
      } catch (error) {
        console.error("Error refreshing data:", error);
      } finally {
        this.loadingRefresh = false;
      }
    },
    async download({ start, end }) {
      this.loadingDownloads = true;
      try {
        await this.getFilePromise(
          "prod_availability_months/export_xls/",
          "",
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "blob",
          { start, end, variety: "w" },
          60000
        );
      } catch (error) {
        console.error("Error downloading the file:", error);
        this.$store.dispatch("snackbar/showError", i18n.global.t("snack.download_error"));
      } finally {
        this.loadingDownloads = false;
        this.exportDialog = false;
      }
    },
  },

  data() {
    const now = moment();
    return {
      moduleName: "prod_availability_months",
      items: [],
      loading: false,
      loadingRefresh: false,
      loadingDownloads: false,
      // Last month of the window shown (the window ends on the current month at first)
      windowEnd: now.format("YYYY-MM"),
      exportDialog: false,
      // Columns of each month, in order (percent ones are color coded)
      columns: [
        { key: "prod", percent: true, decimals: 1 },
        { key: "data_turnover", percent: true, decimals: 1 },
        { key: "time", percent: true, decimals: 1 },
        { key: "data", percent: true, decimals: 0 },
        { key: "production", percent: false },
      ],
      thresholds: { ok: 97, warn: 90 },
      // Months loaded and shown at once (the arrows move the window), no side scroll
      monthsPerPage: 3,
      producerFilter: [],
      // month: null = sort by producer name; metric: one of the columns keys
      sort: { month: null, metric: null, desc: false },
    };
  },
};
</script>

<style scoped>
.producer-search {
  min-width: 18em;
  max-width: 28em;
}
.history-table :deep(th.sortable) {
  cursor: pointer;
  user-select: none;
}
.history-table :deep(th.sortable:hover) {
  color: rgb(var(--v-theme-primary));
}
.history-table :deep(th .sort-icon) {
  opacity: 0.35;
}
.history-table :deep(th.active .sort-icon) {
  opacity: 1;
  color: rgb(var(--v-theme-primary));
}


/* Period navigation in the toolbar: a pill with the arrows at both ends */
.period-nav {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 20px;
  padding: 0 2px;
  background: #fafafa;
}
.period-nav__label {
  min-width: 15em;
  padding: 0 8px;
  font-size: 0.95rem;
  font-weight: 500;
  white-space: nowrap;
}

/* The table fits the window: the visible months share the width, no side scroll */
.history-table :deep(.v-table__wrapper) {
  overflow-x: hidden;
}
.history-table :deep(table) {
  table-layout: fixed;
  width: 100%;
}
/* Both header rows stay visible while scrolling (the second one sits under the first) */
.history-table :deep(thead th) {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
}
.history-table :deep(thead tr:nth-child(2) th) {
  top: 36px;
}
.history-table :deep(thead th.sticky-column) {
  z-index: 3;
}
.history-table :deep(table) {
  font-family: "Roboto", "Helvetica Neue", Arial, sans-serif;
}
.history-table :deep(th),
.history-table :deep(td) {
  padding: 6px 10px !important;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 1rem;
  font-variant-numeric: tabular-nums;
}
.history-table :deep(td.cell) {
  font-size: 1.15rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  cursor: default;
}
.history-table :deep(td.producer-col) {
  font-size: 1rem;
}
.history-table :deep(th.month-head) {
  height: 36px;
  font-weight: 700;
  font-size: 1rem;
  text-transform: capitalize;
  border-left: 1px solid rgba(0, 0, 0, 0.12);
}
.history-table :deep(th.sub-head) {
  font-weight: 600;
  font-size: 0.8rem;
  letter-spacing: 0.02em;
  color: rgba(0, 0, 0, 0.6);
}
.history-table :deep(.month-start) {
  border-left: 1px solid rgba(0, 0, 0, 0.12);
}
.history-table :deep(tbody tr:nth-child(even) td) {
  background-color: #fafafa;
}
.history-table :deep(tbody tr:hover td) {
  background-color: #f0f7fa;
}

.producer-col {
  width: 11em;
}
.sticky-column {
  position: sticky;
  left: 0;
  z-index: 2;
  background: #fff;
  border-right: 1px solid rgba(0, 0, 0, 0.12);
}

/* Availability levels */
.cell.level-ok {
  background-color: #e8f5e9 !important;
  color: #1b5e20;
}
.cell.level-warn {
  background-color: #fff3e0 !important;
  color: #e65100;
}
.cell.level-bad {
  background-color: #ffebee !important;
  color: #b71c1c;
  font-weight: 600;
}
.legend-swatch {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 3px;
  border: 1px solid rgba(0, 0, 0, 0.12);
}
.legend-swatch.level-ok {
  background-color: #e8f5e9;
}
.legend-swatch.level-warn {
  background-color: #fff3e0;
}
.legend-swatch.level-bad {
  background-color: #ffebee;
}
</style>
